import { Guid } from '../../infrastructure/guid'
import { api } from '../../infrastructure/api'
import {
    Deal, DealAssignee, DealListItem, Incoterm, Plant, PlantParams, PricingPeriodListItem, ProductSapCode, ProductSapCodeParams,
    SapCounterparty, SapCounterpartyDealType, SapCounterpartyDealTypeParams, SapShipTo, SapVendor, SapVendorParams,
    SearchFields, ShipToParams, Company, Counterparty, Site, SupplyProduct, DuplicateDealCommand, CreateMovementFromDeal,
    DealForecastMktSales
} from './dealModels'
import { DealLabelId, MeanOfTransportation } from '../stock/stockModels'
import { PriceIndex } from '../pricings/pricingModels'
import { QuantityUnitPrice } from '../administration/masterData/quantityUnitPrice'
import { PricingPeriodRule } from '../administration/masterData/pricingPeriodRule'

function search(filtersQuery: string | null): Promise<DealListItem[]> {
    let route = filtersQuery ? `deal?${filtersQuery}` : 'deal'
    return api.get<DealListItem[]>(route)
}

function get(id: Guid, waitCorrelarionId?: string): Promise<Deal> {
    return api.get<Deal>(`deal/${id}`, undefined, undefined, waitCorrelarionId)
}

function searchDealsForMovement(company: string, date: string, productId: Guid): Promise<DealLabelId[]> {
    return api.get<DealLabelId[]>(`deal/formovement?company=${company}&date=${date}&productId=${productId}`)
}

function searchCounterpartys(country: string, includeInternals: boolean): Promise<Counterparty[]> {
    return api.get<Counterparty[]>(`deal/searchCounterpartys?country=${country}&includeInternals=${includeInternals}`)
}

function getPricingPeriodRules(): Promise<PricingPeriodRule[]> {
    return api.get<PricingPeriodRule[]>(`deal/pricingPeriodRules`)
}

function getSites(): Promise<Site[]> {
    return api.get<Site[]>('deal/site')
}

function getCompanys(): Promise<Company[]> {
    return api.get<Company[]>('deal/company')
}

function getProductCodes(): Promise<SupplyProduct[]> {
    return api.get<SupplyProduct[]>('deal/product')
}

function getPaymentTerms(): Promise<{ code: string }[]> {
    return api.get<{ code: string }[]>('deal/paymentTerm')
}

function getPlantCode(parameters: PlantParams): Promise<Plant[]> {
    return api.get<Plant[]>('deal/plant', parameters)
}

function getSapVendorCode(parameters: SapVendorParams): Promise<SapVendor[]> {
    return api.get<SapVendor[]>('deal/sapVendor', parameters)
}

function getSapCounterpartyShipTo(parameters: ShipToParams): Promise<SapShipTo[]> {
    return api.get<SapShipTo[]>('deal/sapShipTo', parameters)
}

function getProductSapCodeByCompanyAndProduct(parameters: ProductSapCodeParams): Promise<ProductSapCode[]> {
    return api.get<ProductSapCode[]>('deal/productSapCodeByCompanyAndProduct', parameters)
}

function getPriceIndexes(company: string, productId: Guid): Promise<PriceIndex[]> {
    return api.get<PriceIndex[]>(`deal/priceIndex/${company}/${productId}`)
}

function getSearchFields(): Promise<SearchFields> {
    return api.get<SearchFields>('deal/searchField')
}

function getAssignees(company: string): Promise<DealAssignee[]> {
    return api.get<DealAssignee[]>('deal/assignee', { company: company })
}

function post(deal: Deal) {
    return api.post('deal', deal)
}

function validate(deal: Deal | null, type: string, comment: string | null) {
    return api.post('deal/validate', { deal: deal, comment, validationProcessName: type })
}

function LinkMovement(dealId: Guid | null, movementId?: Guid | null) {
    return api.post('deal/linkMovement', { dealId: dealId, movementId: movementId })
}

function refuse(deal: Deal | null, type: string, comment: string | null) {
    return api.post('deal/refuse', { deal: deal, comment, validationProcessName: type })
}

function canValidate(dealId: Guid, type: string): Promise<boolean> {
    return api.get<boolean>(`deal/canValidate/${dealId}/${type}`)
}

function batchDelete(dealIds: Guid[], deleteMovements: boolean) {
    return api.post('deal/batchDelete', { dealIds: dealIds, deleteMovements: deleteMovements })
}

function getDealIdFromMovementId(movementId: Guid): Promise<Guid | null> {
    return api.get<Guid | null>(`deal/id/fromMovement/${movementId}`)
}

function getDealLabelIdFromMovementId(movementId: Guid): Promise<DealLabelId | null> {
    return api.get<DealLabelId | null>(`deal/idLabel/fromMovement/${movementId}`)
}

function duplicateDeal(command: DuplicateDealCommand) {
    return api.post('deal/duplicate', command, undefined)
}

function assignMovements(dealId?: Guid, movementIds?: string[]) {
    return api.post(`deal/${dealId}/movement/batchAssign`, { dealId, movementIds })
}

async function unassignMovement(dealId?: Guid, movementId?: Guid) {
    return api.del(`deal/${dealId}/movement/${movementId}`, { dealId: dealId, movementId: movementId })
}

function getDealTypes(): Promise<string[]> {
    return api.get<string[]>('deal/type')
}

function createMovementFromDeal(command: CreateMovementFromDeal) {
    return api.post('deal/movement', command)
}

function getQuantityCurrencyUnitPrices(): Promise<QuantityUnitPrice[]> {
    return api.get<QuantityUnitPrice[]>('deal/quantityCurrencyUnitPrice')
}

function getMot(): Promise<MeanOfTransportation[]> {
    return api.get<MeanOfTransportation[]>('deal/meanOfTransportation')
}

function applyMktSaleForecast(command: DealForecastMktSales) {
    return api.post('deal/mktSalesForecast', command)
}

function getSapCounterpartys(): Promise<SapCounterparty[]> {
    return api.get<SapCounterparty[]>('deal/sapCounterParty')
}

function getSapCounterpartyDealTypes(parameters: SapCounterpartyDealTypeParams): Promise<SapCounterpartyDealType[]> {
    return api.get<SapCounterpartyDealType[]>('deal/sapCounterpartyDealType', parameters)
}

function createDailyDealMovements(dealId?: Guid) {
    return api.post('deal/createDailyMovement', { dealId: dealId })
}

function getForecastDailyPurchaseStatus(dealId: Guid): Promise<{ status: string }> {
    return api.get<{ status: string }>(`deal/forecastDailyPurchaseStatus/${dealId}`)
}

function refreshInternalDealsFromMktSales(allCountry: boolean) {
    return api.post('deal/mktSalesDeals', { allCountry: allCountry })
}

function getAllIncoterms() {
    return api.get<Incoterm[]>('deal/incoterm')
}

function searchPricingPeriods(filtersQuery: string | null): Promise<PricingPeriodListItem[]> {
    var route = filtersQuery ? `deal/pricingPeriod?${filtersQuery}` : 'deal/pricingPeriod'
    return api.get<PricingPeriodListItem[]>(route)
}

function getAllDas() {
    return api.get<string[]>('deal/das')
}

function getDefaultQuantityPriceUnit(company: string, productId: Guid, movementType: string, dealType: string) {
    let query = `deal/quantityPriceUnit?companyCode=${company}&productId=${productId}&movementType=${movementType}&dealType=${dealType}`
    return api.get<{ quantityPriceUnit: string | null }>(query)
}

export const dealApi = {
    search,
    get,
    searchDealsForMovement,
    searchCounterpartys,
    getSites,
    getCompanys,
    getProductCodes,
    getPaymentTerms,
    getPlantCode,
    getSapVendorCode,
    getSapCounterpartyShipTo,
    getProductSapCodeByCompanyAndProduct,
    getPriceIndexes,
    getSearchFields,
    getAssignees,
    post,
    validate,
    LinkMovement,
    refuse,
    canValidate,
    batchDelete,
    getDealIdFromMovementId,
    getDealLabelIdFromMovementId,
    duplicateDeal,
    assignMovements,
    unassignMovement,
    getDealTypes,
    createMovementFromDeal,
    getQuantityCurrencyUnitPrices,
    getMot,
    applyMktSaleForecast,
    getSapCounterpartys,
    getSapCounterpartyDealTypes,
    createDailyDealMovements,
    getForecastDailyPurchaseStatus,
    refreshInternalDealsFromMktSales,
    getAllIncoterms,
    searchPricingPeriods,
    getAllDas,
    getPricingPeriodRules,
    getDefaultQuantityPriceUnit
}